/**
 * @file Service pour la gestion des organisations.
 * @description
 * Ce service permet aux utilisateurs de gérer leur organisation :
 * récupération des informations, mise à jour du nom, gestion des
 * informations légales (SIRET, contacts, CGV, mentions légales),
 * et personnalisation du branding email.
 */
import { apiService } from "./api.service";
import { ORGANIZATIONS } from "../config/api.endpoints";
import type { ApiComponents } from "@/types/api";
import type {
  EmailBranding,
  EmailBrandingUpdate,
  GoogleReviewStatisticsResponse,
  GoogleReviewProcessResponse,
} from "@/types/api";

export type { EmailBranding, EmailBrandingUpdate };

type Organization = ApiComponents["schemas"]["Organization"];
type OrganizationUpdate = ApiComponents["schemas"]["OrganizationUpdate"];
type OrganizationLegalUpdate = ApiComponents["schemas"]["OrganizationLegalUpdate"];
type GoogleReviewSettings = ApiComponents["schemas"]["GoogleReviewSettings"];
type GoogleReviewStatistics = GoogleReviewStatisticsResponse;

export const OrganizationsService = {
  /**
   * Récupère l'organisation de l'utilisateur connecté
   */
  async getMyOrganization(): Promise<Organization> {
    return apiService.get<Organization>(ORGANIZATIONS.ME);
  },

  /**
   * Met à jour le nom et le slug de l'organisation
   */
  async updateOrganization(
    organizationUpdate: OrganizationUpdate
  ): Promise<Organization> {
    return apiService.patch<Organization>(
      ORGANIZATIONS.ME,
      organizationUpdate
    );
  },

  /**
   * Met à jour les informations légales de l'organisation
   * (SIRET, contacts, CGV, mentions légales)
   */
  async updateOrganizationLegal(
    legalUpdate: OrganizationLegalUpdate
  ): Promise<Organization> {
    return apiService.patch<Organization>(
      ORGANIZATIONS.LEGAL,
      legalUpdate
    );
  },

  /**
   * Met à jour seulement le SIRET de l'organisation
   */
  async updateSiret(siret: string): Promise<Organization> {
    return this.updateOrganizationLegal({ siret });
  },

  /**
   * Met à jour les informations de contact de l'organisation
   */
  async updateContactInfo(
    emailContact?: string,
    phoneContact?: string
  ): Promise<Organization> {
    return this.updateOrganizationLegal({
      email_contact: emailContact,
      phone_contact: phoneContact,
    });
  },

  /**
   * Met à jour le contenu des CGV
   */
  async updateCGV(cgvContent: string): Promise<Organization> {
    return this.updateOrganizationLegal({ cgv_content: cgvContent });
  },

  /**
   * Met à jour le contenu des mentions légales
   */
  async updateLegalMentions(
    legalMentionsContent: string
  ): Promise<Organization> {
    return this.updateOrganizationLegal({
      legal_mentions_content: legalMentionsContent,
    });
  },

  // ============================================
  // Email Branding Methods
  // ============================================

  /**
   * Retrieves the email branding configuration for the current organization.
   * Returns default values if no custom branding is configured.
   */
  async getEmailBranding(): Promise<EmailBranding> {
    return apiService.get<EmailBranding>(ORGANIZATIONS.EMAIL_BRANDING);
  },

  /**
   * Updates the email branding configuration.
   * Only provided fields will be updated.
   */
  async updateEmailBranding(
    brandingUpdate: EmailBrandingUpdate
  ): Promise<EmailBranding> {
    return apiService.patch<EmailBranding>(
      ORGANIZATIONS.EMAIL_BRANDING,
      brandingUpdate
    );
  },

  /**
   * Updates only the sender name for emails.
   */
  async updateEmailSenderName(senderName: string): Promise<EmailBranding> {
    return this.updateEmailBranding({ sender_name: senderName });
  },

  /**
   * Updates only the primary color for emails.
   */
  async updateEmailPrimaryColor(primaryColor: string): Promise<EmailBranding> {
    return this.updateEmailBranding({ primary_color: primaryColor });
  },

  // ============================================
  // Google Reviews Methods
  // ============================================

  /**
   * Retrieves the Google reviews settings for the current organization.
   */
  async getGoogleReviewSettings(): Promise<GoogleReviewSettings> {
    return apiService.get<GoogleReviewSettings>(ORGANIZATIONS.GOOGLE_REVIEWS_SETTINGS);
  },

  /**
   * Updates the Google reviews settings.
   */
  async updateGoogleReviewSettings(
    settings: GoogleReviewSettings
  ): Promise<GoogleReviewSettings> {
    return apiService.patch<GoogleReviewSettings>(
      ORGANIZATIONS.GOOGLE_REVIEWS_SETTINGS,
      settings
    );
  },

  /**
   * Retrieves Google reviews statistics for the current organization.
   */
  async getGoogleReviewStatistics(
    startDate?: string,
    endDate?: string
  ): Promise<GoogleReviewStatistics> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const url = `${ORGANIZATIONS.GOOGLE_REVIEWS_STATISTICS}?${params.toString()}`;
    return apiService.get<GoogleReviewStatistics>(url);
  },

  /**
   * Manually processes pending Google review requests (for testing).
   */
  async processPendingGoogleReviewRequests(): Promise<GoogleReviewProcessResponse> {
    return apiService.post<GoogleReviewProcessResponse>(ORGANIZATIONS.GOOGLE_REVIEWS_PROCESS, {});
  },
};

export default OrganizationsService;
